/**
 * Pure data-join core — records in → {parcels, permitRows, inspectionRows, unitRows, lotIndex,
 * inspCountByPermitNumber, syncedAt} out. Extracted from useParcels so the riskiest logic in the
 * app (the cross-table joins: stage-rank-first sales winner, canonical-permit pick, re-inspection
 * supersedence, syncedAt fallbacks) is testable in node against plain mock records — no React,
 * no Airtable SDK. useParcels stays a thin shell that feeds live useRecords output through here.
 *
 * "Record" here is anything with { id, getCellValue(fieldId), getCellValueAsString(fieldId) } —
 * the guarded accessors below (cv/sStr/…) tolerate throws and misses, so a withheld/renamed
 * field degrades one datapoint instead of crashing the map. The harness passes plain objects.
 */
import { LOT, PERMIT, UNIT, INSPECTION, SALES, MODEL, DRAWING, PUNCH, LOTUPDATE, CHANGELOG } from '../ids';
import { orderBandOf } from './lenses';
import { parseGeo, geoArea, Geo } from './geometry';
import { parseDate } from '../util/date';
import { stageRank } from './stageRank';
import { computeLotPrice, PriceBreakdown } from './pricing';
export type { PriceBreakdown } from './pricing';

// ---- Dock-console row shapes (record grain, joined) — consumed by the bottom-dock grids ----
export interface LotMeta { name: string; blockLot: string; }
export interface RowBase {
  id: string; lotId: string | null; lotIds: string[]; lotName: string; blockLot: string;
}
export interface PermitRow extends RowBase {
  number: string; permitUrl: string; model: string; urgencyBand: string; openClosed: string; actionRank: number | null;
  pctToCO: number | null; closeoutStage: string; schedule: string; nextInsp: string; nextSched: string;
  amountDue: number | null; feeBalance: number | null; daysToExpiration: number | null; expirationDate: string;
  blocks: boolean; owner: string; sla: string; coVerified: boolean; coUrl: string; inspectionIds: string[];
  activeOperating: boolean;
  changedAt: string; changeBucket: string; changeType: string; changeSummary: string; firstSeenAt: string;
  appliedDate: string; issueDate: string; completedDate: string; failedInspections: number | null;
  signedPermitDocs: Attachment[]; nocDocs: Attachment[];
  void: boolean;  // county marked the permit "VOID Deleted …" in its description → a dead permit
}
export interface InspectionRow extends RowBase {
  permitNumber: string; type: string; phase: string; result: string; resultBucket: string;
  opStatus: string; opRank: number | null; actionWindow: string; calendarDate: string; scheduledDate: string;
  completedDate: string; inspector: string; isLatest: boolean; isNext: boolean; reinspection: boolean; permitUrl: string;
  superseded: boolean; // a later inspection of the SAME (permit, type) passed → this failed/partial visit is resolved
  comments: string;
}
/** Essentials of a superseded GHL sales row — kept so the offer history is visible (capped at 10). */
export interface SalesHistoryEntry { status: string; value: number | null; date: string; agent: string; }
export interface SalesRow {
  id: string; unitId: string | null; buyerName: string; ghlStatus: string;
  matchedValue: number | null; agent: string; agentEmail: string; lastStageChangeAt: string;
  applicationStatus: string; interviewStatus: string; escrowWaiver: string; createdAt: string;
  packageSelection: string; executedContract: Attachment[]; executedAddendum: Attachment[];
  financialIncentives: string; financialIncentiveUse: string; financialIncentiveNotes: string;
  constructionUpgradeNotes: string;
  jobContingency: string; jobNotes: string;
  additionalContingencies: string; additionalContingencyNotes: string;
  depositUponSigning: number | null; initialDepositPct: number | null;
  escrowReceivedDate: string; contractExecutionDate: string;
  escrowStatus: string; totalDepositedInEscrow: number | null; escrowReceipt: Attachment[];
  basePurchasePrice: number | null;
  // Buyer-specific scoped credits (editable Sales overrides; null ⇒ no credit set).
  upgradeCredit: number | null; upgradePctOff: number | null; baseCredit: number | null;
  /** The WINNING buyer's own earlier offer rows on this unit, most-recent-first (capped at 10).
   *  Never contains other buyers — losing buyers are dissociated, not shown as lot history. */
  history: SalesHistoryEntry[];
}
export interface UnitRow extends RowBase {
  unitName: string; model: string; sqft: number | null; gpd: number | null; contractStatus: string; contractStatusLive: string;
  buyer: string; finalValue: number | null; upgradeValue: number | null;
  buildStatus: string; buildPhase: string; rankPriority: number | null; modelHome: boolean; sales: SalesRow | null;
  sqftLiving: number | null; sqftAttic: number | null; sqftGarage: number | null; sqftLot: number | null;
  baseModelValue: number | null; buildOrder: number | null; pkg: string; version: string; modelVersion: string;
  /** GHL physical construction upgrades for this unit, comma-joined (from the GHL Inbox). */
  constructionUpgrade: string;
  /** Build % Complete (v5) — 0..1 fraction; null when untracked (no Build Stage assigned). */
  percentComplete: number | null;
  /** Computed Final Sales Price from the parcel's priceBreakdown — joined into unitRows in useParcels
   *  after buildDockData (which has no parcel access). null when base purchase price is unknown. */
  finalSalesPrice?: number | null;
}
/** Punch List row (record grain). The table links to a UNIT, so `unitName` is the unit and the
 *  lot is resolved one hop further (Unit → Lot) so a punch row flies to its lot like every other
 *  child grid. Photos are exposed as Attachment[] so DocsCell re-signs URLs at click time. */
export interface PunchRow extends RowBase {
  punchId: string; unitId: string | null; unitName: string; model: string;
  masterPhase: string; subPhase: string; issue: string; status: string; priority: string;
  aging: string; daysOpen: number | null; company: string; punchEmail: string;
  reportedBy: string; sentDate: string; closedDate: string; resolutionNotes: string;
  photos: Attachment[];
}
/** Lot Updates row (record grain). Like Punch, the table links a UNIT, so the lot is resolved one
 *  hop further (Unit → Lot, via the pre-built unitLotIndex) so the update flies to / filters by its
 *  lot. Photos are Attachment[] (re-signed at render). The detail feed sorts newest-first by
 *  `created` (createdTime — there is no manual date field). */
export interface LotUpdateRow extends RowBase {
  unitId: string | null; unitName: string; title: string; description: string;
  visibility: string; status: string; buyerNotified: boolean;
  submittedBy: string; created: string; photos: Attachment[];
}
/** Field-change Log row (record grain) from the change tracker. The changed record's id is plain
 *  TEXT; for Units changes it resolves to a lot via the same Unit→Lot index Punch/Updates use, so a
 *  field change "flies to" its lot. `created` = the record's createdTime (the hourly detection time). */
export interface ChangeLogRow {
  id: string; lotId: string | null; lotIds: string[]; unitId: string | null;
  tableName: string; fieldName: string; oldValue: string; newValue: string; eventType: string; created: string;
}
export interface Attachment {
  id: string; filename: string; url: string; type: string; thumbUrl: string; // url/thumbUrl = join-time snapshots (cell-value URLs expire ~2h)
  clientUrl: string;   // getter: re-reads the LIVE record's current url + signs at access time — read at render/click, never store
  clientThumb: string; // getter: signed thumbnail URL, same freshness contract
}
export interface DrawingSet { label: string; files: Attachment[]; }
export interface ModelProjection {
  name: string; photo: Attachment | null; video: Attachment | null; matterport: string;
  stories: number | null; beds: number | null; baths: number | null;
  sfLiving: number | null; sfAttic: number | null; sfGarage: number | null;
  baseCost: number | null; countyValue: number | null;
  drawings: DrawingSet[]; // grouped by discipline, empty sets dropped
}
export interface DockData {
  permitRows: PermitRow[]; inspectionRows: InspectionRow[]; unitRows: UnitRow[]; punchRows: PunchRow[];
  lotUpdateRows: LotUpdateRow[]; changeLogRows: ChangeLogRow[];
  lotIndex: Map<string, LotMeta>; inspCountByPermitNumber: Map<string, number>;
  salesByUnit?: Map<string, SalesRow>;   // winning sale per unit id — reused by joinAll for parcel-level lens fields
  syncedAt?: { permits: number | null; sales: number | null };
}

// Master build stage = Build Stage (v5) parent (formula fldzYtMwNvp8y8JnN). The 7 real phase groups
// + legacy 'Rough' (a still-valid v5 phase value some units carry) + both casings of the not-triaged fallback.
export type BuildPhase = 'Done' | 'Finishes' | 'Drywall' | 'Interior Rough' | 'Dry-in Rough'
  | 'Rough' | 'Shell' | 'Foundation' | 'Not started' | 'Not triaged' | 'Not Triaged';
export type SalesBucket =
  | 'Escrow' | 'Under contract' | 'Requested' | 'Sent' | 'Prospect' | 'Available' | 'Cancelled' | 'N/A';

export interface UnitProjection {
  id: string; name: string; model: string; buyer: string;
  contractStatus: string; contractStatusLive: string; finalValue: number | null;
  sqft: number | null; gpd: number | null; buildStatus: string; buildPhase: BuildPhase;
  orientation: string; version: string; modelVersion: string; pkg: string;
  ghlContractStatus: string; concurrency: string; rankPriority: number | null;
  percentComplete: number | null;   // "Percent Complete (v5)" — 0..1 fraction (null = untracked/no build stage)
}

export interface Parcel {
  id: string; name: string; block: number | null; lot: number | null; blockLot: string;
  geo: Geo;
  // lens drivers
  landUseCategory: string; buildStatus: string | null; buildPhase: BuildPhase;
  salesBucket: SalesBucket; masterPhase: string; subPhase: string;
  // closeout (canonical permit) — formerly "Unknown/Missing"
  pctToCO: number | null; closeoutStage: string | null; closeoutStageNum: number | null;
  inspStatus: string | null; inspStatusRaw: string | null; urgencyBand: string | null; urgencyRank: number | null;
  openClosed: string;  // 'Open' | 'Open / Superseded' | 'Completed' | 'Closed' — '' = no permit (lens → 'No permit')
  nextInspection: string | null; actionStatus: string | null; actionRank: number | null;
  permitNumber: string | null; permitUrl: string | null; permitStatus: string | null;
  // Canonical permit milestone dates — surfaced for the Activity feed timeline (raw strings, '' = none).
  permitAppliedDate: string; permitIssuedDate: string; permitCompletedDate: string; permitExpirationDate: string;
  signedPermitDocs: Attachment[]; nocDocs: Attachment[];   // canonical permit's signed PDF + NOC attachments
  expired: boolean; feeBalance: number | null; blocksConstruction: boolean; permitCount: number;
  // unit / sales / site
  unit: UnitProjection | null; model: string; lotSizeSF: number | null; surveyAttached: boolean; linkedUnits: number;
  buyersAll: string;  // every buyer across ALL linked units — primary-only made duplex second buyers unsearchable
  permitIssued: string;  // 'Issued' | 'Applied / not issued' | 'No permit' — map filter group
  lakeview: boolean; sidewalk: boolean; easement: boolean; hasSiteMarkers: boolean; countyClarType: string;
  newParcelNumber: string;  // county's new parcel/folio number (trimmed)
  address: string;          // street address, multiline collapsed to one comma-joined line

  // inspection rollup
  inspNext: string | null;
  inspections: InspRow[];
  // derived
  isException: boolean; exceptionReason: string | null;
  buildOrder: number | null; buildOrderBucket: string;
  modelData: ModelProjection | null;  // per-model marketing + spec data (joined by model name)
  allModels: string;                  // DISTINCT models across ALL linked units (multi-unit lots: "Shul #1, Mikvah #1")
  gpdAll: number; sqftAll: number; valueAll: number; // summed across ALL linked units — primary-only undercounted multi-unit lots
  pctComplete: number | null;        // avg Build % Complete (0..1) across this lot's units (untracked unit = 0); null if lot has no units
  // Per-model breakdown of this lot's units (model name → its units' summed measures). Lets the
  // Model lens attribute each model its OWN value/GPD/SF instead of an even lot-total ÷ tokenCount
  // split (models on one lot have very different real values). Empty for lot-name-only fallbacks.
  modelMeasures: Map<string, { units: number; sf: number; gpd: number; val: number }>;
  constructionUpgrade: string;        // GHL physical construction upgrades, NEWLINE-joined (names can contain commas)
  financialIncentives: string;        // GHL financial incentives, comma-joined
  jobContingency: string;             // GHL job contingency, comma-joined
  additionalContingencies: string;    // GHL additional contingencies, comma-joined
  escrowStatus: string;               // GHL escrow status (winning sale), single value — backs the Escrow status filter
  escrowReceivedDate: string;         // GHL escrow received date (winning sale) — drives the Escrow received Yes/No filter
  priceBreakdown: PriceBreakdown;     // computed per-lot: base + upgrades − incentives → Final Sales Price
}

export interface InspRow { type: string; result: string; bucket: string; isLatest: boolean; isNext: boolean; sortDate: string; }

// Interface extensions can hand the element a bound table that exposes only a SUBSET of
// the real table's fields. Reading a field that isn't exposed THROWS — which would blank
// the whole map. So every cell read goes through these guarded accessors (return empty on
// any miss), and useRecords is only ever asked for fields the bound table actually has
// (see `fieldsOf` below). One drifted/withheld field can degrade a datapoint, never crash.
export const cv = (r: any, f: string): any => { try { return r ? r.getCellValue(f) : null; } catch { return null; } };
// Truthy read that's safe for FORMULA booleans — a formula can yield the string "0"/"false",
// which `!!` would wrongly treat as true. Handles real checkboxes, numbers, and lookup arrays.
const _truthy = (x: any): boolean => x === true || x === 1 || (typeof x === 'string' && /^(1|true|yes|y)$/i.test(x.trim()));
export const bln = (r: any, f: string): boolean => { const v = cv(r, f); return Array.isArray(v) ? v.some(_truthy) : _truthy(v); };
export const sStr = (r: any, f: string): string => { try { return r ? (r.getCellValueAsString(f) || '') : ''; } catch { return ''; } };
// County voids a permit by prefixing its Project Description with "VOID Deleted …" (the monitor
// preserves that text). A void permit is dead — excluded from open/gating/attention everywhere.
export const isVoidText = (s: string): boolean => /\b(void|deleted)\b/i.test(s || '');
export const num = (r: any, f: string): number | null => {
  const v = cv(r, f);
  // Lookup/rollup fields (e.g. "GPD (Concurrency)") return an ARRAY of values — sum the
  // numeric entries (a single-value lookup just returns that value). Without this, every
  // lookup-number read fell through to null and aggregated to 0.
  if (Array.isArray(v)) {
    const ns = v.map((x: any) => typeof x === 'number' ? x : (x && typeof x === 'object' ? Number(x.value ?? x.name) : Number(x))).filter((n: number) => !isNaN(n));
    return ns.length ? ns.reduce((a, b) => a + b, 0) : null;
  }
  if (typeof v === 'number') return v;
  if (typeof v === 'string') { const m = v.replace(/[, ]/g, '').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; }
  return null;
};
export const linkIds = (r: any, f: string): string[] => {
  const v = cv(r, f); return Array.isArray(v) ? v.map((x: any) => x.id) : [];
};
/** Read a multipleSelects (or multi-value) cell → array of trimmed choice NAMES. Unlike sStr
 *  (which joins with ", " and would shred names that themselves contain commas), this preserves
 *  each choice as one token — callers join with '\n' to keep the newline-delimited contract. */
export const selNames = (r: any, f: string): string[] => {
  const v = cv(r, f);
  if (!Array.isArray(v)) return [];
  return v.map((x: any) => (x && typeof x === 'object' ? String(x.name ?? x.value ?? '') : String(x))).map(s => s.trim()).filter(Boolean);
};
export const attachCount = (r: any, f: string): number => {
  const v = cv(r, f); return Array.isArray(v) ? v.length : 0;
};
/** Read an attachment (or lookup-of-attachment) field → normalized list. Carries the RAW cell-value
 *  URL + attachment id; interface extensions reject the raw URL for rendering, so the UI signs it
 *  at render/click time via the record's airtableInterface. No SDK calls happen at join time:
 *  signing inside the data-build useMemo caused a render loop that froze the app.
 *
 *  URL EXPIRY: Airtable attachment cell-value URLs expire (~2h). The join runs only when
 *  useRecords delivers a change (the parcels/dock memos depend on the record arrays), so `url`
 *  is a join-time SNAPSHOT that can outlive its signature in an idle tab. The clientUrl /
 *  clientThumb getters therefore RE-READ the current cell value from the live record at access
 *  time — find this attachment id again, take its CURRENT url — before signing, so a click or
 *  re-render after Airtable re-signs never serves the stale snapshot. (If the SDK itself has had
 *  no fresher value pushed, the snapshot is the best anyone can do client-side; the signed client
 *  URL is the SDK-blessed rendering route per the official docs.) */
export const atts = (r: any, f: string): Attachment[] => {
  try {
    const v = cv(r, f); if (!Array.isArray(v)) return [];
    const flatten = (x: any[]): any[] => x.flatMap((y: any) => Array.isArray(y) ? y : [y]); // lookups can nest arrays
    const flat = flatten(v);
    const canSign = typeof r?.getAttachmentClientUrlFromCellValueUrl === 'function';
    return flat.filter((a: any) => a && a.url).map((a: any) => {
      const id = a.id || a.url, rawUrl = a.url || '';
      const rawThumb = a.thumbnails?.large?.url || a.thumbnails?.small?.url || '';
      // Freshest version of THIS attachment the SDK currently holds (falls back to the
      // join-time snapshot if the cell no longer lists the id, or the read throws).
      const current = (): any => {
        try {
          const cur = cv(r, f);
          if (!Array.isArray(cur)) return a;
          return flatten(cur).find((x: any) => x && x.id && x.id === a.id) || a;
        } catch { return a; }
      };
      // Sign ONLY when the getter is read (render/click time). Doing it in the useMemo froze the app.
      const sign = (u: string) => { if (!u) return ''; if (!canSign) return u; try { return r.getAttachmentClientUrlFromCellValueUrl(id, u); } catch { return u; } };
      return {
        id, filename: a.filename || 'file', type: a.type || '', url: rawUrl, thumbUrl: rawThumb,
        get clientUrl() { return sign(current().url || rawUrl); },
        get clientThumb() { const c = current(); return sign(c.thumbnails?.large?.url || c.thumbnails?.small?.url || rawThumb); },
      } as Attachment;
    });
  } catch { return []; }
};
/** Restrict a useRecords field request to fields the bound table actually exposes, so the
 *  query never throws on a withheld/renamed field. Returns the opts object for useRecords. */
export const fieldsOf = (table: any, ids: string[]) => ({
  fields: ids.filter(id => { try { return !!(table && table.getFieldByIdIfExists && table.getFieldByIdIfExists(id)); } catch { return false; } }),
});

export function salesBucketFromSummary(summary: string): SalesBucket {
  const s = (summary || '').toLowerCase();
  if (!s || s === 'n/a' || s === 'na') return 'N/A';
  if (s.includes('escrow received') || (s.includes('escrow') && !s.includes('waiting'))) return 'Escrow';
  if (s.includes('countersigned')) return 'Under contract';
  if (s.includes('client requested')) return 'Requested';
  if (s.includes('sent to client')) return 'Sent';
  if (s.includes('waiting for request')) return 'Prospect';
  if (s.includes('cancelled') || s.includes('canceled')) return 'Cancelled';
  if (s.includes('available')) return 'Available';
  return 'N/A';
}

/** Choose between two parsed geometries (lfe-tracer `Map Polygon JSON` vs lfe-static-plat
 *  `Map JSON Meta`). The tracer field flattens donut/multipart tracts into one outer-only
 *  ring with no holes — so for Park Tract 3 its ring footprint is ~1.94x its own area_sf,
 *  painting over the hole. Preference order:
 *   1) if exactly one carries holes/parts, take it (only it can express the donut);
 *   2) else, when both report area_sf, take whichever's NET footprint (outer minus holes)
 *      best matches its own area_sf — exposing the flattened/inflated ring as the worse fit;
 *   3) else fall back to the tracer field (a), then the meta field (b). */
export function pickGeo(a: Geo | null, b: Geo | null): Geo | null {
  if (!a) return b;
  if (!b) return a;
  const has = (g: Geo) => g.holes.length > 0 || g.parts.length > 0;
  if (has(a) !== has(b)) return has(a) ? a : b;
  // Net-area fidelity: |footprint − area_sf| / area_sf (smaller = the ring actually matches
  // the stated acreage). Only decisive when a field reports its own area_sf.
  const err = (g: Geo): number | null =>
    g.areaSf && g.areaSf > 0 ? Math.abs(geoArea(g) - g.areaSf) / g.areaSf : null;
  const ea = err(a), eb = err(b);
  if (ea != null && eb != null && Math.abs(ea - eb) > 1e-6) return ea < eb ? a : b;
  return a;
}

function inspBucket(raw: string): string {
  const s = (raw || '').toLowerCase();
  if (!s) return '';
  if (s.includes('expired')) return 'Expired';
  if (s.includes('overdue') || s.includes('past due')) return 'Overdue';
  if (s.includes('blind') || s.includes('no next') || s.includes('unknown next')) return 'Blind';
  if (s.includes('schedule')) return 'Scheduled';
  if (s.includes('closed') || s.includes('complete') || s.includes('co ')) return 'Closed';
  if (s.includes('non-building') || s.includes('not a building') || s.includes('n/a')) return 'Non-building';
  return 'Scheduled';
}

/** Physical construction upgrades per unit, keyed by Unit record id — read from the canonical
 *  Units "Construction Upgrades" multi-select (UNIT.constructionUpgrades). Choice names are joined
 *  with '\n' to preserve the newline-delimited contract every downstream consumer expects (Color By
 *  lens, filter tokenizer, and computeLotPrice's per-upgrade split). Replaces the prior GHL Sync
 *  Inbox free-text source (2026-06-27). */
export function buildUpgradesByUnit(units: any[] | null | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const u of (units || [])) {
    const names = selNames(u, UNIT.constructionUpgrades);
    if (names.length) map.set(u.id, names.join('\n'));
  }
  return map;
}

export interface ParcelJoinInput {
  lots: any[]; permits?: any[] | null; units?: any[] | null; insps?: any[] | null;
  upgrades?: any[] | null; models?: any[] | null; drawings?: any[] | null;
  upgradesByUnit?: Map<string, string>;
  financialIncentivesByUnit?: Map<string, string>;
  jobContingencyByUnit?: Map<string, string>;
  additionalContingenciesByUnit?: Map<string, string>;
  escrowStatusByUnit?: Map<string, string>;
  escrowReceivedByUnit?: Map<string, string>;
  upgradePricing?: Map<string, number>;
  creditsByUnit?: Map<string, { upgradeCredit: number; pctOff: number; baseCredit: number }>;
  basePriceByUnit?: Map<string, number>;
}

/** Lot-grain projection: geometry + canonical permit + primary unit + model/drawings join. */
export function buildParcels(input: ParcelJoinInput): Parcel[] {
  const { lots, permits, units, insps, models, drawings } = input;
  const upgradesByUnit = input.upgradesByUnit || new Map<string, string>();
  const financialIncentivesByUnit = input.financialIncentivesByUnit || new Map<string, string>();
  const jobContingencyByUnit = input.jobContingencyByUnit || new Map<string, string>();
  const additionalContingenciesByUnit = input.additionalContingenciesByUnit || new Map<string, string>();
  const escrowStatusByUnit = input.escrowStatusByUnit || new Map<string, string>();
  const escrowReceivedByUnit = input.escrowReceivedByUnit || new Map<string, string>();
  const upgradePricing = input.upgradePricing || new Map<string, number>();
  const creditsByUnit = input.creditsByUnit || new Map<string, { upgradeCredit: number; pctOff: number; baseCredit: number }>();
  const basePriceByUnit = input.basePriceByUnit || new Map<string, number>();
  const permitById = new Map<string, any>((permits || []).map((r: any) => [r.id, r]));
  const unitById = new Map<string, any>((units || []).map((r: any) => [r.id, r]));
  const inspByPermit = new Map<string, any[]>();
  (insps || []).forEach(r => {
    for (const pid of linkIds(r, INSPECTION.permitLink)) {
      if (!inspByPermit.has(pid)) inspByPermit.set(pid, []);
      inspByPermit.get(pid)!.push(r);
    }
  });

  // Drawings read DIRECT from the Drawings table → grouped by Discipline per model record id.
  // Each PDF is owned by its drawing record, so atts(dr, …) signs it cleanly (lookup attachments
  // on the Model don't sign reliably). Sorted latest-first within each discipline.
  // Eric's order: Architectural → Interior Design → Structural → MEP → Trusses (variants included).
  const DISCIPLINE_ORDER = ['Architectural', 'Architecture', 'Interior Design', 'Interior Design (Basic)', 'Interior Design (Upgrade)', 'Structural', 'MEP', 'MEPs', 'Trusses'];
  const drawingsByModelId = new Map<string, Map<string, Attachment[]>>();
  for (const dr of (drawings || [])) {
    if (sStr(dr, DRAWING.latestVersion) !== 'Latest') continue; // only the latest revision per discipline
    const files = atts(dr, DRAWING.pdf); if (!files.length) continue;
    const disc = sStr(dr, DRAWING.discipline) || 'Other';
    for (const mid of linkIds(dr, DRAWING.model)) {
      if (!drawingsByModelId.has(mid)) drawingsByModelId.set(mid, new Map());
      const byDisc = drawingsByModelId.get(mid)!;
      byDisc.set(disc, [...(byDisc.get(disc) || []), ...files]);
    }
  }
  const drawingSetsFor = (mid: string): DrawingSet[] => {
    const byDisc = drawingsByModelId.get(mid); if (!byDisc) return [];
    const keys = [...byDisc.keys()].sort((a, b) => {
      const ia = DISCIPLINE_ORDER.indexOf(a), ib = DISCIPLINE_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
    return keys.map(k => ({ label: k, files: byDisc.get(k)! }));
  };

  // Model lookup (joined by name) — marketing photo/video, 3D tour, specs, drawing sets.
  const modelByName = new Map<string, ModelProjection>();
  for (const mr of (models || [])) {
    const nm = sStr(mr, MODEL.name); if (!nm) continue;
    // Prefer the direct Drawings-table read; fall back to the Model's lookup-attachment fields.
    const set = (label: string, f: string): DrawingSet | null => { const files = atts(mr, f); return files.length ? { label, files } : null; };
    const lookupDrawings = [
      set('Architecture', MODEL.dwgArchitecture), set('MEP', MODEL.dwgMep),
      set('Structural', MODEL.dwgStructural), set('Trusses', MODEL.dwgTrusses),
      set('Interior · Basic', MODEL.dwgInteriorBasic), set('Interior · Upgrade', MODEL.dwgInteriorUpgrade),
    ].filter(Boolean) as DrawingSet[];
    const direct = drawingSetsFor(mr.id);
    const drawingSets = direct.length ? direct : lookupDrawings;
    modelByName.set(nm, {
      name: nm, photo: atts(mr, MODEL.marketingPhoto)[0] || null, video: atts(mr, MODEL.marketingVideo)[0] || null,
      matterport: sStr(mr, MODEL.matterport),
      stories: num(mr, MODEL.stories), beds: num(mr, MODEL.bedrooms), baths: num(mr, MODEL.bathrooms),
      sfLiving: num(mr, MODEL.sfLiving), sfAttic: num(mr, MODEL.sfAttic), sfGarage: num(mr, MODEL.sfGarage),
      baseCost: num(mr, MODEL.baseCost), countyValue: num(mr, MODEL.countyValue), drawings: drawingSets,
    });
  }

  const out: Parcel[] = [];
  for (const lr of lots) {
    const geo = pickGeo(parseGeo(sStr(lr, LOT.mapPolygonJson)), parseGeo(sStr(lr, LOT.mapJsonMeta)));
    if (!geo) continue;

    // canonical permit
    const pIds = linkIds(lr, LOT.permitsLink);
    const pRecs = pIds.map(id => permitById.get(id)).filter(Boolean) as any[];
    const canonical = pickCanonical(pRecs);
    const inspForCanonical = canonical ? (inspByPermit.get(canonical.id) || []) : [];

    // primary unit (deterministic: prefer the sellable/highest-priority unit, not link order)
    const uIds = linkIds(lr, LOT.unitsLink);
    const uProjected = (uIds.map(id => unitById.get(id)).filter(Boolean) as any[]).map(u => projectUnit(u));
    const unit = pickPrimaryUnit(uProjected);

    // Parcel master build stage comes SOLELY from the primary unit's own v5 fields — the granular
    // Build Stage (v5) and its Build Phase PARENT (formula fldzYtMwNvp8y8JnN), so "Master Build
    // Stage" reconciles with the granular "Build Stage" lens (both off the same v5 source:
    // Foundation/Shell/Dry-in Rough/Interior Rough/Drywall/Finishes/Done). The legacy Lots
    // "Build Status" fallback was retired 2026-06-27 — a lot with no linked unit reads 'Not triaged'.
    const buildStatus = unit ? unit.buildStatus : null;
    const buildPhase = unit ? unit.buildPhase : 'Not triaged';
    const salesBucket = salesBucketFromSummary(sStr(lr, LOT.salesSummary) || (unit ? unit.contractStatus : ''));

    const pct = canonical ? num(canonical, PERMIT.pctToCO) : null;
    const inspRaw = canonical ? sStr(canonical, PERMIT.inspScheduleStatus) : null;
    const band = canonical ? sStr(canonical, PERMIT.closeoutUrgencyBand) : null; // e.g. "01 Critical"
    // Open/Closed lifecycle (from the canonical permit's formula). A permit with an empty value
    // defaults to 'Open'; no permit at all stays '' so the lens reads "No permit".
    const openClosed = canonical ? (sStr(canonical, PERMIT.openClosed) || 'Open') : '';
    const bandRank = band ? (parseInt(band.slice(0, 2), 10) || null) : null;
    const stageRaw = canonical ? sStr(canonical, PERMIT.closeoutStage) : null;   // e.g. "10 Lath & drywall"
    const stageNum = stageRaw && stageRaw.match(/^\d+/) ? parseInt(stageRaw.match(/^\d+/)![0], 10) : null;
    const days = canonical ? num(canonical, PERMIT.daysToExpiration) : null;
    const feeBal = canonical ? num(canonical, PERMIT.feeBalance) : null;
    const permitVoid = canonical ? isVoidText(sStr(canonical, PERMIT.description)) : false;
    const blocks = canonical && !permitVoid ? bln(canonical, PERMIT.blocksConstruction) : false;  // a voided permit never gates
    const expired = days != null && days < 0;

    const committed = salesBucket === 'Escrow' || salesBucket === 'Under contract' || salesBucket === 'Requested';
    // A voided permit ("VOID Deleted …") is dead — none of the PERMIT-derived exceptions apply
    // (but a committed-sale-not-started flag, which is sale-derived, still can).
    let exReason: string | null = null;
    if (!permitVoid && bandRank === 1) exReason = 'Critical — expired before closeout';
    else if (!permitVoid && bandRank === 2) exReason = 'Overdue — inspection result missing';
    else if (!permitVoid && expired) exReason = 'Permit expired';
    // NOTE: "Blocks Construction? = 1" is the NORMAL state for every live building
    // permit (verified against the base: true for all active bands 02/04/05), so it is
    // not an anomaly — flagging it marked 100% of permitted lots. Kept as data on the
    // parcel, but removed from the exception heuristic.
    else if (!permitVoid && feeBal != null && feeBal > 0) exReason = 'Outstanding fee balance';
    else if (committed && (buildPhase === 'Not triaged' || buildPhase === 'Not started')) exReason = 'Committed sale · not started';

    const constructionUpgrade = (uIds.map(id => upgradesByUnit.get(id)).find(Boolean)) || '';
    const financialIncentives = (uIds.map(id => financialIncentivesByUnit.get(id)).find(Boolean)) || '';
    const parcelModel = sStr(lr, LOT.model) || (unit ? unit.model : '');
    const base = (uIds.map(id => basePriceByUnit.get(id)).find(v => v != null)) ?? null;
    const credits = (uIds.map(id => creditsByUnit.get(id)).find(Boolean)) || { upgradeCredit: 0, pctOff: 0, baseCredit: 0 };
    const priceBreakdown = computeLotPrice({
      base, model: parcelModel, constructionUpgrades: constructionUpgrade, upgradePricing,
      upgradeCredit: credits.upgradeCredit, pctOff: credits.pctOff, baseCredit: credits.baseCredit,
    });

    out.push({
      id: lr.id, name: sStr(lr, LOT.name),
      block: num(lr, LOT.block), lot: num(lr, LOT.lotNo), blockLot: sStr(lr, LOT.blockLot),
      geo,
      landUseCategory: sStr(lr, LOT.landUseCategory) || geo.designation,
      buildStatus, buildPhase, salesBucket,
      masterPhase: geo.masterPhase || sStr(lr, LOT.masterPhase), subPhase: geo.subPhase || sStr(lr, LOT.subPhase),
      pctToCO: pct, closeoutStage: stageRaw, closeoutStageNum: stageNum,
      inspStatus: inspRaw ? inspBucket(inspRaw) : null, inspStatusRaw: inspRaw,
      urgencyBand: band, urgencyRank: bandRank, openClosed,
      nextInspection: canonical ? sStr(canonical, PERMIT.nextExpectedInspection) : null,
      actionStatus: canonical ? sStr(canonical, PERMIT.closeoutActionStatus) : null,
      actionRank: canonical ? num(canonical, PERMIT.closeoutActionRank) : null,
      permitNumber: canonical ? sStr(canonical, PERMIT.number) : null,
      permitUrl: canonical ? sStr(canonical, PERMIT.permitUrl) : null,
      permitStatus: canonical ? sStr(canonical, PERMIT.currentStatus) : null,
      permitAppliedDate: canonical ? sStr(canonical, PERMIT.appliedDate) : '',
      permitIssuedDate: canonical ? sStr(canonical, PERMIT.issueDate) : '',
      permitCompletedDate: canonical ? sStr(canonical, PERMIT.completionDate) : '',
      permitExpirationDate: canonical ? sStr(canonical, PERMIT.expirationDate) : '',
      signedPermitDocs: canonical ? atts(canonical, PERMIT.signedPermit) : [], nocDocs: canonical ? atts(canonical, PERMIT.noc) : [],
      expired, feeBalance: feeBal, blocksConstruction: blocks, permitCount: pRecs.length,
      unit, model: parcelModel,
      buyersAll: uProjected.map(u => u.buyer).filter(Boolean).join('  '),
      permitIssued: pRecs.length ? (pRecs.some(pr => sStr(pr, PERMIT.issueDate)) ? 'Issued' : 'Applied / not issued') : 'No permit',
      lotSizeSF: num(lr, LOT.squareFeet), surveyAttached: attachCount(lr, LOT.survey) > 0,
      linkedUnits: uIds.length, lakeview: bln(lr, LOT.lakeview), sidewalk: bln(lr, LOT.sidewalk), easement: bln(lr, LOT.easement),
      hasSiteMarkers: linkIds(lr, LOT.siteMarkers).length > 0, countyClarType: sStr(lr, LOT.countyClarificationType),
      newParcelNumber: sStr(lr, LOT.newParcelNumber).trim(),
      // Address is entered multiline ("1949 Rashi Dr.\nOkeechobee, FL 34974"); collapse to one
      // comma-joined line so it reads cleanly in a Row and the search haystack matches on it.
      address: sStr(lr, LOT.address).split(/\n+/).map(s => s.trim()).filter(Boolean).join(', '),
      inspNext: pickInsp(inspForCanonical, INSPECTION.isNext),
      inspections: inspForCanonical.map(r => ({
        type: sStr(r, INSPECTION.type), result: sStr(r, INSPECTION.result), bucket: sStr(r, INSPECTION.resultBucket),
        isLatest: bln(r, INSPECTION.isLatest), isNext: bln(r, INSPECTION.isNext),
        // date used to pick the LATEST inspection per type for the map's inspection-status filter
        sortDate: sStr(r, INSPECTION.completedDate) || sStr(r, INSPECTION.scheduledDate) || '',
      })),
      isException: !!exReason, exceptionReason: exReason,
      buildOrder: null, buildOrderBucket: 'No build order',
      modelData: modelByName.get(parcelModel) || null,
      constructionUpgrade,
      financialIncentives,
      jobContingency: (uIds.map(id => jobContingencyByUnit.get(id)).find(Boolean)) || '',
      additionalContingencies: (uIds.map(id => additionalContingenciesByUnit.get(id)).find(Boolean)) || '',
      escrowStatus: (uIds.map(id => escrowStatusByUnit.get(id)).find(Boolean)) || '',
      escrowReceivedDate: (uIds.map(id => escrowReceivedByUnit.get(id)).find(Boolean)) || '',
      allModels: [...new Set(uProjected.map(u => u.model).filter(Boolean))].join(', ') || sStr(lr, LOT.model),
      gpdAll: uProjected.reduce((s, u) => s + (u.gpd || 0), 0),
      sqftAll: uProjected.reduce((s, u) => s + (u.sqft || 0), 0),
      valueAll: uProjected.reduce((s, u) => s + (u.finalValue || 0), 0),
      // Build % Complete: mean of this lot's units' duration-weighted %, treating an untracked
      // unit (null — no Build Stage yet) as 0% (= not started). null only when the lot has no
      // linked units (not buildable), so the phase rollup denominator is buildable lots.
      pctComplete: uProjected.length ? uProjected.reduce((s, u) => s + (u.percentComplete || 0), 0) / uProjected.length : null,
      // Per-model measures from the SAME uProjected array — sums to valueAll/sqftAll/gpdAll
      // exactly, so the Model lens's per-token shares preserve the children-sum-to-parent
      // invariant. Empty when the lot has no units (allModels falls back to the LOT.model name).
      modelMeasures: (() => {
        const m = new Map<string, { units: number; sf: number; gpd: number; val: number }>();
        for (const u of uProjected) {
          if (!u.model) continue;
          const e = m.get(u.model) || { units: 0, sf: 0, gpd: 0, val: 0 };
          e.units += 1; e.sf += u.sqft || 0; e.gpd += u.gpd || 0; e.val += u.finalValue || 0;
          m.set(u.model, e);
        }
        return m;
      })(),
      priceBreakdown,
    });
  }

  // Build Order — rank lots whose primary unit has a Rank Priority AND a sellable value
  // (excludes unvalued placeholders), ascending = builds first; assign a 1..N sequence
  // number and bucket into Earliest→Latest quintiles (red→orange→yellow→green→blue heat map).
  const ranked = out.filter(p => p.unit && p.unit.rankPriority != null && p.unit.finalValue != null && p.unit.finalValue > 0)
    .sort((a, b) => (a.unit!.rankPriority as number) - (b.unit!.rankPriority as number));
  const RN = ranked.length;
  ranked.forEach((p, i) => {
    p.buildOrder = i + 1;
    p.buildOrderBucket = orderBandOf(RN > 1 ? i / (RN - 1) : 0);
  });
  return out;
}

export interface DockJoinInput {
  lots?: any[] | null; permits?: any[] | null; units?: any[] | null;
  insps?: any[] | null; sales?: any[] | null; punch?: any[] | null; lotUpdates?: any[] | null;
  changeLog?: any[] | null;
}

/** Dock-console child rows (record grain), derived from each child table's own Lot link. */
export function buildDockData(input: DockJoinInput): DockData {
  const { lots, permits, units, insps, sales, punch, lotUpdates, changeLog } = input;
  // Physical construction upgrades per unit — keyed by Unit record id, from the canonical Units
  // multi-select (UNIT.constructionUpgrades).
  const upgradesByUnit = buildUpgradesByUnit(units);
  const lotIndex = new Map<string, LotMeta>();
  for (const lr of (lots || [])) lotIndex.set(lr.id, { name: sStr(lr, LOT.name), blockLot: sStr(lr, LOT.blockLot) });

  const baseOf = (r: any, lotField: string): RowBase => {
    const ids = linkIds(r, lotField);
    const lotId = ids[0] || null;
    const meta = lotId ? lotIndex.get(lotId) : null;
    return { id: r.id, lotId, lotIds: ids, lotName: (meta && meta.name) || '', blockLot: (meta && meta.blockLot) || '' };
  };

  // Unit → Lot index: the Punch table links a UNIT, not a Lot, so resolve each punch's lot one
  // hop further (Unit → Lot, the same link the Units tab reads). Built once from the units records.
  const unitLotIndex = new Map<string, { lotId: string | null; lotIds: string[] }>();
  for (const ur of (units || [])) {
    const ids = linkIds(ur, UNIT.lotsLink);
    unitLotIndex.set(ur.id, { lotId: ids[0] || null, lotIds: ids });
  }

  // All permits (no longer hard-filtered to Interface-Safe); the dock's "Safe only" toggle
  // applies that filter on demand, with interfaceSafe carried on each row.
  const permitRows: PermitRow[] = (permits || []).map((r: any) => ({
    ...baseOf(r, PERMIT.lotLink),
    activeOperating: bln(r, PERMIT.activeOperating),
    number: sStr(r, PERMIT.number), permitUrl: sStr(r, PERMIT.permitUrl), model: sStr(r, PERMIT.modelFromLot),
    urgencyBand: sStr(r, PERMIT.closeoutUrgencyBand), openClosed: sStr(r, PERMIT.openClosed), actionRank: num(r, PERMIT.closeoutActionRank),
    pctToCO: num(r, PERMIT.pctToCO), closeoutStage: sStr(r, PERMIT.closeoutStage),
    schedule: sStr(r, PERMIT.inspScheduleStatus), nextInsp: sStr(r, PERMIT.nextExpectedInspection),
    nextSched: sStr(r, PERMIT.nextScheduledInspection), amountDue: num(r, PERMIT.amountDue),
    feeBalance: num(r, PERMIT.feeBalance), daysToExpiration: num(r, PERMIT.daysToExpiration),
    expirationDate: sStr(r, PERMIT.expirationDate), blocks: bln(r, PERMIT.blocksConstruction),
    owner: sStr(r, PERMIT.actionOwner), sla: sStr(r, PERMIT.actionSlaStatus),
    coVerified: bln(r, PERMIT.coCertVerified), coUrl: sStr(r, PERMIT.coCertUrl),
    inspectionIds: linkIds(r, PERMIT.inspectionsLink),
    changedAt: sStr(r, PERMIT.changedAt), changeBucket: sStr(r, PERMIT.changeBucket), firstSeenAt: sStr(r, PERMIT.firstSeenAt),
    changeType: sStr(r, PERMIT.changeType), changeSummary: sStr(r, PERMIT.changeSummary),
    appliedDate: sStr(r, PERMIT.appliedDate), issueDate: sStr(r, PERMIT.issueDate), completedDate: sStr(r, PERMIT.completionDate),
    failedInspections: num(r, PERMIT.openFailedInspections),
    signedPermitDocs: atts(r, PERMIT.signedPermit), nocDocs: atts(r, PERMIT.noc),
    void: isVoidText(sStr(r, PERMIT.description)),
  }));

  const inspectionRows: InspectionRow[] = (insps || []).filter((r: any) => bln(r, INSPECTION.opInclude)).map((r: any) => ({
    ...baseOf(r, INSPECTION.lotLink),
    permitNumber: sStr(r, INSPECTION.permitNumber), type: sStr(r, INSPECTION.type), phase: sStr(r, INSPECTION.phase),
    result: sStr(r, INSPECTION.result), resultBucket: sStr(r, INSPECTION.resultBucket),
    opStatus: sStr(r, INSPECTION.opStatus), opRank: num(r, INSPECTION.opRank), actionWindow: sStr(r, INSPECTION.actionWindow),
    calendarDate: sStr(r, INSPECTION.calendarDate), scheduledDate: sStr(r, INSPECTION.scheduledDate),
    completedDate: sStr(r, INSPECTION.completedDate), inspector: sStr(r, INSPECTION.inspector),
    isLatest: bln(r, INSPECTION.isLatest), isNext: bln(r, INSPECTION.isNext), reinspection: bln(r, INSPECTION.reinspection),
    permitUrl: sStr(r, INSPECTION.permitUrlFromPermit), superseded: false,
    comments: sStr(r, INSPECTION.comments),
  }));

  // Resolve re-inspections: if a LATER inspection of the same (permit, type) passed, the earlier
  // failed OR partial visit is cleared. Mark those rows superseded so they drop out of the
  // re-insp queue. (Partials included 2026-06-12 — the county flags partials into its
  // reinspection queue too; live audit found 5 partial rows still counted "open" whose type had
  // already passed, e.g. Footer/Foundation rec7iF1KGBPCrgUHv partial 4/1 → later pass.)
  // Group key: permit#+type; when permit# is blank fall back to LOT+type (not the row's own id,
  // which would put every row in its own group and never let a later pass clear a failure).
  const inspKey = (ir: InspectionRow) => (ir.permitNumber || ir.lotId || ir.id) + '|' + ir.type;
  const latestOf = new Map<string, { t: number; pass: boolean }>();
  for (const ir of inspectionRows) {
    // Compare by epoch, not raw string: a date field that renders non-ISO (e.g. M/D/YYYY)
    // would order wrong under lexicographic '>'. parseDate tolerates every format Airtable
    // renders (ISO, US, locale 12-hour dateTime); blank → 0.
    const t = parseDate(ir.completedDate || ir.calendarDate || ir.scheduledDate);
    const cur = latestOf.get(inspKey(ir));
    if (!cur || t > cur.t) latestOf.set(inspKey(ir), { t, pass: /pass/i.test(ir.resultBucket || ir.result) });
  }
  for (const ir of inspectionRows) {
    const latest = latestOf.get(inspKey(ir));
    ir.superseded = !!(latest && latest.pass && /fail|partial/i.test(ir.resultBucket || ir.result));
  }

  // Sales join — ONE winner per unit, STAGE-RANK FIRST (owner's buyer-precedence rule: the
  // buyer further along the sale process is the only buyer shown; lost/cancelled never beats
  // an active row), recency tie-break within a stage. The GHL sync already unlinks losing
  // buyers' Sales rows, so this join usually sees a single row per unit — the ranking here is
  // defense-in-depth for the window between a GHL change and the next sync run.
  // Recency is parseDate epoch, NOT string compare: the lookup renders locale 12-hour strings
  // ("6/4/2026 1:05pm") that Date.parse rejects whole — every row would read 0 and the FIRST
  // row would win. Ties/blanks keep encounter order (stable sort).
  const salesListByUnit = new Map<string, SalesRow[]>();
  for (const r of (sales || [])) {
    const unitId = linkIds(r, SALES.unit)[0] || null;
    if (!unitId) continue;
    const row: SalesRow = {
      id: r.id, unitId, buyerName: sStr(r, SALES.buyerName),
      ghlStatus: sStr(r, SALES.ghlContractStatus),
      matchedValue: num(r, SALES.matchedValue), agent: sStr(r, SALES.agent), agentEmail: sStr(r, SALES.agentEmail),
      lastStageChangeAt: sStr(r, SALES.lastStageChangeAt),
      applicationStatus: sStr(r, SALES.applicationStatus), interviewStatus: sStr(r, SALES.interviewStatus),
      escrowWaiver: sStr(r, SALES.escrowWaiver), createdAt: sStr(r, SALES.createdAt),
      packageSelection: sStr(r, SALES.packageSelection), executedContract: atts(r, SALES.executedContract),
      financialIncentives: sStr(r, SALES.ghlFinancialIncentives),
      financialIncentiveUse: sStr(r, SALES.ghlFinancialIncentiveUse),
      financialIncentiveNotes: sStr(r, SALES.ghlFinancialIncentiveNotes),
      constructionUpgradeNotes: sStr(r, SALES.ghlConstructionUpgradeNotes),
      jobContingency: sStr(r, SALES.ghlJobContingency),
      jobNotes: sStr(r, SALES.ghlJobNotes),
      additionalContingencies: sStr(r, SALES.ghlAdditionalContingencies),
      additionalContingencyNotes: sStr(r, SALES.ghlAdditionalContingencyNotes),
      depositUponSigning: num(r, SALES.ghlDepositUponSigning),
      initialDepositPct: num(r, SALES.ghlInitialDepositPct),
      escrowReceivedDate: sStr(r, SALES.ghlEscrowReceivedDate),
      contractExecutionDate: sStr(r, SALES.ghlContractExecutionDate),
      escrowStatus: sStr(r, SALES.ghlEscrowStatus),
      totalDepositedInEscrow: num(r, SALES.ghlTotalDepositedInEscrow),
      escrowReceipt: atts(r, SALES.escrowReceipt),
      executedAddendum: atts(r, SALES.executedAddendum),
      basePurchasePrice: num(r, SALES.basePurchasePrice),
      // Editable Sales overrides (authoritative). The parsed-from-GHL values are kept SEPARATE
      // (ghl*) so the panel can do override-else-parsed without altering the global contract.
      upgradeCredit: num(r, SALES.upgradeCredit),
      upgradePctOff: num(r, SALES.upgradePctOff),
      baseCredit: num(r, SALES.baseCredit),
      history: [],
    };
    const list = salesListByUnit.get(unitId);
    if (list) list.push(row); else salesListByUnit.set(unitId, [row]);
  }
  const rowStage = (p: SalesRow) => stageRank(p.ghlStatus);
  const buyerKey = (p: SalesRow) => (p.buyerName || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const salesByUnit = new Map<string, SalesRow>();
  for (const [unitId, list] of salesListByUnit) {
    list.sort((a, b) => (rowStage(b) - rowStage(a)) || (parseDate(b.lastStageChangeAt) - parseDate(a.lastStageChangeAt)));
    const winner = list[0];
    // History = the winning buyer's OWN earlier offers only (matched on the normalized buyer
    // name, the Sales primary field). Other buyers' rows never appear as lot history — the
    // owner's rule dissociates losers entirely. Blank names can't be confirmed as the same
    // buyer, so they are excluded rather than guessed.
    const wk = buyerKey(winner);
    winner.history = list.slice(1).filter(p => wk !== '' && buyerKey(p) === wk).slice(0, 10).map(p => ({
      status: p.ghlStatus, value: p.matchedValue, date: p.lastStageChangeAt, agent: p.agent,
    }));
    salesByUnit.set(unitId, winner);
  }

  const unitRows: UnitRow[] = (units || []).map((r: any) => ({
    ...baseOf(r, UNIT.lotsLink),
    unitName: sStr(r, UNIT.name), model: sStr(r, UNIT.model), sqft: num(r, UNIT.squareFeet), gpd: num(r, UNIT.gpd),
    contractStatus: sStr(r, UNIT.contractStatus), contractStatusLive: sStr(r, UNIT.contractStatusLive), buyer: sStr(r, UNIT.buyer),
    finalValue: num(r, UNIT.finalValue), upgradeValue: num(r, UNIT.upgradeValue),
    buildStatus: sStr(r, UNIT.buildStageV5), buildPhase: sStr(r, UNIT.buildPhase), rankPriority: num(r, UNIT.rankPriority),
    modelHome: bln(r, UNIT.modelHome), sales: salesByUnit.get(r.id) || null,
    sqftLiving: num(r, UNIT.sqftLiving), sqftAttic: num(r, UNIT.sqftAttic), sqftGarage: num(r, UNIT.sqftGarage),
    sqftLot: num(r, UNIT.sqftLot), baseModelValue: num(r, UNIT.baseModelValue), buildOrder: num(r, UNIT.buildOrder), pkg: sStr(r, UNIT.unitPackage),
    version: sStr(r, UNIT.unitVersion), modelVersion: sStr(r, UNIT.unitModelVersion),
    constructionUpgrade: upgradesByUnit.get(r.id) || '',
    percentComplete: num(r, UNIT.percentCompleteV5),
  }));

  // Punch List rows — lot resolved via Unit → Lot (the punch table links a unit, not a lot).
  const punchRows: PunchRow[] = buildPunch(punch, unitLotIndex, lotIndex);

  // Lot Updates rows — same Unit → Lot hop as Punch (the table links a unit). Feed the detail panel.
  const lotUpdateRows: LotUpdateRow[] = buildLotUpdates(lotUpdates, unitLotIndex, lotIndex);

  // Field-change Log rows — the changed record id is plain text; Units changes resolve to a lot.
  const changeLogRows: ChangeLogRow[] = buildChangeLog(changeLog, unitLotIndex);

  const inspCountByPermitNumber = new Map<string, number>();
  for (const ir of inspectionRows) {
    if (!ir.permitNumber) continue;
    inspCountByPermitNumber.set(ir.permitNumber, (inspCountByPermitNumber.get(ir.permitNumber) || 0) + 1);
  }
  return { permitRows, inspectionRows, unitRows, punchRows, lotUpdateRows, changeLogRows, lotIndex, inspCountByPermitNumber, salesByUnit };
}

/** Field-change Log rows for the Activity feed. The changed record's id (CHANGELOG.changedRecordId)
 *  is plain text; for Units-table changes it's a Unit id, resolved to a lot via the same Unit→Lot
 *  index Punch/Updates use. Record-added/removed events and changes that don't resolve to a lot are
 *  dropped (the per-lot feed only shows field changes on this lot's unit). Every cell read is guarded
 *  (un-exposed field → blank, never throws). `created` = the record's createdTime (a Date in the SDK,
 *  an ISO string in tests), normalized to ISO. */
export function buildChangeLog(
  rows: any[] | null | undefined,
  unitLotIndex: Map<string, { lotId: string | null; lotIds: string[] }>,
): ChangeLogRow[] {
  const out: ChangeLogRow[] = [];
  for (const r of (rows || [])) {
    if (sStr(r, CHANGELOG.eventType) === 'record_added') continue;   // not a field-level event
    const recId = sStr(r, CHANGELOG.changedRecordId).trim();
    const ul = recId ? unitLotIndex.get(recId) : null;               // only Units changes resolve to a lot
    if (!ul || !ul.lotId) continue;
    // The interface hands the extension FIELD-GATED records: the intrinsic `record.createdTime`
    // getter touches `_data` and THROWS on those ("Direct access to _data not allowed; use
    // getCellValue"). Guard it so reading the event time can NEVER crash the element — a change row
    // whose time we can't read simply drops from the feed. (Proper dated events need an explicit
    // "Created time"/"Logged At" field on the Log table read via getCellValue — follow-up.)
    let created = '';
    try { const ct: any = (r as any).createdTime; created = ct instanceof Date ? ct.toISOString() : (typeof ct === 'string' ? ct : ''); }
    catch { /* field-gated record — no direct createdTime; event drops without a date */ }
    out.push({
      id: r.id, lotId: ul.lotId, lotIds: ul.lotIds, unitId: recId,
      tableName: sStr(r, CHANGELOG.tableName), fieldName: sStr(r, CHANGELOG.fieldName),
      oldValue: sStr(r, CHANGELOG.oldValue), newValue: sStr(r, CHANGELOG.newValue),
      eventType: sStr(r, CHANGELOG.eventType), created,
    });
  }
  return out;
}

/** Punch List rows (record grain). Every cell read goes through the GUARDED accessors, so an
 *  un-exposed field reads blank and NEVER throws. The lot is resolved one hop further than the
 *  other grids: punch → Unit (PUNCH.unit) → Lot (via the pre-built unitLotIndex: Unit → Lot link),
 *  so clicking a punch row flies to its lot. Photos are normalized to Attachment[] so DocsCell
 *  re-signs the URLs at click time (atts()'s clientUrl getter). Exported for the harness. */
export function buildPunch(
  punch: any[] | null | undefined,
  unitLotIndex: Map<string, { lotId: string | null; lotIds: string[] }>,
  lotIndex: Map<string, LotMeta>,
): PunchRow[] {
  return (punch || []).map((r: any) => {
    const unitLink = cv(r, PUNCH.unit);
    const unitIds = linkIds(r, PUNCH.unit);
    const unitId = unitIds[0] || null;
    // Unit display name: the link cell carries [{id,name}]; sStr is the tolerant fallback.
    const unitName = (Array.isArray(unitLink) ? unitLink.map((x: any) => x && x.name).filter(Boolean).join(', ') : '') || sStr(r, PUNCH.unit);
    const ul = unitId ? unitLotIndex.get(unitId) : null;
    const lotIds = ul ? ul.lotIds : [];
    const lotId = ul ? ul.lotId : null;
    const meta = lotId ? lotIndex.get(lotId) : null;
    return {
      id: r.id, lotId, lotIds, lotName: (meta && meta.name) || '', blockLot: (meta && meta.blockLot) || sStr(r, PUNCH.blockLot),
      punchId: sStr(r, PUNCH.punchId), unitId, unitName,
      model: sStr(r, PUNCH.model), masterPhase: sStr(r, PUNCH.masterPhase), subPhase: sStr(r, PUNCH.subPhase),
      issue: sStr(r, PUNCH.issue), status: sStr(r, PUNCH.status), priority: sStr(r, PUNCH.priority),
      aging: sStr(r, PUNCH.aging), daysOpen: num(r, PUNCH.daysOpen),
      company: sStr(r, PUNCH.company), punchEmail: sStr(r, PUNCH.punchEmail), reportedBy: sStr(r, PUNCH.reportedBy),
      sentDate: sStr(r, PUNCH.sentDate), closedDate: sStr(r, PUNCH.closedDate), resolutionNotes: sStr(r, PUNCH.resolutionNotes),
      photos: atts(r, PUNCH.photos),
    };
  });
}

/** Lot Updates rows (record grain). Mirrors buildPunch: every cell read goes through the GUARDED
 *  accessors (an un-exposed field reads blank, never throws), and the lot is resolved one hop past
 *  the link the update carries: update → Unit (LOTUPDATE.unit) → Lot (via the unitLotIndex). Photos
 *  are Attachment[] so the panel re-signs URLs at click time. Exported for the harness. */
export function buildLotUpdates(
  updates: any[] | null | undefined,
  unitLotIndex: Map<string, { lotId: string | null; lotIds: string[] }>,
  lotIndex: Map<string, LotMeta>,
): LotUpdateRow[] {
  return (updates || []).map((r: any) => {
    const unitLink = cv(r, LOTUPDATE.unit);
    const unitIds = linkIds(r, LOTUPDATE.unit);
    const unitId = unitIds[0] || null;
    const unitName = (Array.isArray(unitLink) ? unitLink.map((x: any) => x && x.name).filter(Boolean).join(', ') : '') || sStr(r, LOTUPDATE.unit);
    const ul = unitId ? unitLotIndex.get(unitId) : null;
    const lotIds = ul ? ul.lotIds : [];
    const lotId = ul ? ul.lotId : null;
    const meta = lotId ? lotIndex.get(lotId) : null;
    return {
      id: r.id, lotId, lotIds, lotName: (meta && meta.name) || '', blockLot: (meta && meta.blockLot) || '',
      unitId, unitName, title: sStr(r, LOTUPDATE.title),
      // Show the AI-refined narrative; fall back to the raw notable-changes text when it hasn't generated yet.
      description: sStr(r, LOTUPDATE.refinedDescription).trim() || sStr(r, LOTUPDATE.description),
      visibility: sStr(r, LOTUPDATE.visibility), status: sStr(r, LOTUPDATE.status),
      buyerNotified: bln(r, LOTUPDATE.buyerNotified),
      submittedBy: sStr(r, LOTUPDATE.submittedBy), created: sStr(r, LOTUPDATE.created),
      photos: atts(r, LOTUPDATE.photos),
    };
  });
}

/** Data freshness: when each pipeline last touched Airtable. Permits — the iWorQ monitor writes
 *  daily, so max(Last Modified) = last monitor run, falling back to the change timestamp (always
 *  exposed for the What-changed column) so the label never goes silent. Sales — max(Last GHL Sync At). */
export function buildSyncedAt(permits?: any[] | null, sales?: any[] | null): { permits: number | null; sales: number | null } {
  let p = 0, s = 0;
  for (const r of (permits || [])) {
    // parseDate, NOT Date.parse: both render as locale 12-hour strings Date.parse rejects.
    const t = parseDate(sStr(r, PERMIT.lastModified)) || parseDate(sStr(r, PERMIT.changedAt));
    if (t > p) p = t;
  }
  for (const r of (sales || [])) {
    // Last GHL Sync At is a multipleLookupValues (dateTime): getCellValue returns an ARRAY of
    // { linkedRecordId, value } objects (not strings), so the old String(v[0]) was "[object Object]"
    // → parseDate failed → sales freshness read as null. Pull each looked-up value, take the max.
    const v = cv(r, SALES.lastSyncAt);
    const arr = Array.isArray(v) ? v : (v != null ? [v] : []);
    for (const item of arr) {
      const raw = (item && typeof item === 'object') ? ((item as any).value ?? (item as any).name ?? '') : item;
      const t = parseDate(String(raw || ''));
      if (t > s) s = t;
    }
  }
  return { permits: p || null, sales: s || null };
}

/** Convenience one-shot join (tests + non-React callers): records in → full projection out. */
export function joinAll(input: ParcelJoinInput & DockJoinInput) {
  const dock = buildDockData(input);
  const upgradesByUnit = input.upgradesByUnit || buildUpgradesByUnit(input.units);
  // The incentive/contingency categories drive Color By + filters at the LOT level. Source them from
  // the WINNING sale's Sales lookups (dock.salesByUnit) — the same exposed, populated fields the
  // lot-details panel reads. Construction Upgrades sources from the canonical Units multi-select
  // (UNIT.constructionUpgrades) via buildUpgradesByUnit above. Explicit overrides via input.* still
  // win (tests).
  const sbu = dock.salesByUnit || new Map<string, SalesRow>();
  const fromSales = (pick: (s: SalesRow) => string): Map<string, string> => {
    const m = new Map<string, string>();
    for (const [unitId, s] of sbu) { const v = pick(s); if (v) m.set(unitId, v); }
    return m;
  };
  const financialIncentivesByUnit = input.financialIncentivesByUnit || fromSales(s => s.financialIncentives);
  const jobContingencyByUnit = input.jobContingencyByUnit || fromSales(s => s.jobContingency);
  const additionalContingenciesByUnit = input.additionalContingenciesByUnit || fromSales(s => s.additionalContingencies);
  const escrowStatusByUnit = input.escrowStatusByUnit || fromSales(s => s.escrowStatus);
  const escrowReceivedByUnit = input.escrowReceivedByUnit || fromSales(s => s.escrowReceivedDate);
  return {
    parcels: buildParcels({ ...input, upgradesByUnit, financialIncentivesByUnit, jobContingencyByUnit, additionalContingenciesByUnit, escrowStatusByUnit, escrowReceivedByUnit }),
    ...dock,
    syncedAt: buildSyncedAt(input.permits, input.sales),
  };
}

export function pickCanonical(recs: any[]): any | null {
  if (!recs.length) return null;
  const rank = (r: any) => { const v = num(r, PERMIT.closeoutActionRank); return v == null ? 9999 : v; };
  const included = recs.filter(r => bln(r, PERMIT.closeoutQueueInclude));
  const pool = included.length ? included : recs;
  return pool.slice().sort((a, b) => rank(a) - rank(b))[0];
}

// For a multi-unit lot, the "primary" unit drives the lot's colour, build-phase and sale
// fallback. Picking the FIRST linked unit is arbitrary (Airtable link order) and unstable —
// a placeholder could win over the real sold unit. Prefer a sellable unit (has a build Rank
// Priority AND a positive value) by lowest rank; else fall back to the first. Single-unit
// lots (the vast majority) are unaffected.
export function pickPrimaryUnit(us: UnitProjection[]): UnitProjection | null {
  if (us.length <= 1) return us[0] || null;
  const sellable = us.filter(u => u.rankPriority != null && u.finalValue != null && u.finalValue > 0)
    .sort((a, b) => (a.rankPriority as number) - (b.rankPriority as number));
  return sellable[0] || us[0];
}

export function projectUnit(r: any): UnitProjection {
  return {
    id: r.id, name: sStr(r, UNIT.name), model: sStr(r, UNIT.model), buyer: sStr(r, UNIT.buyer),
    contractStatus: sStr(r, UNIT.contractStatus), contractStatusLive: sStr(r, UNIT.contractStatusLive),
    finalValue: num(r, UNIT.finalValue), sqft: num(r, UNIT.squareFeet), gpd: num(r, UNIT.gpd),
    buildStatus: sStr(r, UNIT.buildStageV5), buildPhase: (sStr(r, UNIT.buildPhase) as BuildPhase) || 'Not triaged',
    orientation: sStr(r, UNIT.houseOrientation), version: sStr(r, UNIT.unitVersion), modelVersion: sStr(r, UNIT.unitModelVersion),
    pkg: sStr(r, UNIT.unitPackage),
    ghlContractStatus: sStr(r, UNIT.ghlContractStatus), concurrency: sStr(r, UNIT.concurrency),
    rankPriority: num(r, UNIT.rankPriority),
    percentComplete: num(r, UNIT.percentCompleteV5),
  };
}

function pickInsp(recs: any[], flagField: string): string | null {
  const hit = recs.find(r => bln(r, flagField));
  return hit ? sStr(hit, INSPECTION.type) || sStr(hit, INSPECTION.result) : null;
}
